import { Col, DatePicker, Form, Select, Spin, Table } from "antd";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Row } from "react-bootstrap";
import { kpiOptions } from "utils/FieldValues";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { getKpiData, setKpiDetailsData } from "redux/reducers/Kpi/kpi.slice";
import dayjs from "dayjs";
import {
  columnsInitial,
  initialKpiInputData,
  formatDate,
  getDatePickerValue,
} from "./kpiHelpers";

const Kpi = () => {
  const location = useLocation();

  const dispatch = useDispatch();

  const { moduleList, selectedLocation } = useSelector(({ role }) => role);

  const { kpiDetails, kpiLoading } = useSelector(({ kpi }) => kpi);

  const [kpiInputData, setKpiInputData] = useState(initialKpiInputData);

  const onChangeKpiInputData = (name, values) => {
    const value =
      name === "start_date" || name === "end_date"
        ? formatDate(values)
        : values;

    setKpiInputData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const selectedModule = useMemo(() => {
    return (
      moduleList?.find((item) => item?.module_name === location?.pathname) || {}
    );
  }, [moduleList, location?.pathname]);

  const callKpiDataAPI = useCallback(() => {
    if (Object.keys(selectedModule)?.length > 0 && selectedLocation) {
      dispatch(
        getKpiData({
          location_id: selectedLocation,
          module_id: selectedModule?._id,
          payload: kpiInputData,
        })
      );
    }
  }, [selectedModule, selectedLocation, dispatch, kpiInputData]);

  const columnsUpdated = useMemo(() => {
    if (Array.isArray(kpiDetails) && kpiDetails.length > 0) {
      return Object.keys(kpiDetails[0]).map((key) => ({
        title:
          key.replace(/_/g, " ").replace(/^./, (m) => m.toUpperCase()) +
          (key !== "kpi_name" ? " (%)" : ""),
        dataIndex: key,
        key: key,
      }));
    }
    return columnsInitial;
  }, [kpiDetails]);

  const dateErrors = useMemo(() => {
    const { start_date, end_date } = kpiInputData;

    if (!start_date || !end_date) return null;

    const start = dayjs(start_date);
    const end = dayjs(end_date);
    const today = dayjs();

    if (start.isAfter(end)) {
      return "Start Date should not be after End Date";
    }

    if (end.isAfter(today)) {
      return "End Date should not be in the future";
    }

    if (end.diff(start, "month", true) > 12) {
      return "Date range should not exceed 12 months";
    }

    return null;
  }, [kpiInputData]);

  useEffect(() => {
    const hasNullValue = Object.values(kpiInputData)?.some(
      (value) => value === null
    );

    if (!hasNullValue && !dateErrors) callKpiDataAPI();
    else dispatch(setKpiDetailsData([]));
  }, [kpiInputData, dateErrors, callKpiDataAPI, dispatch]);

  return (
    <>
      {kpiLoading ? (
        <Spin tip="Loading" size="large">
          <div className="content" />
        </Spin>
      ) : (
        <div className="page_main_content">
          <div className="page_inner_content">
            <div className="page_header">
              <Row className="align-items-center">
                <h3>Key Performance Indicator</h3>
              </Row>
            </div>

            <div className="form_process_wrapper p-0">
              <Row>
                <Col lg={6} md={6} sm={12}>
                  <Form.Item label="Select KPI" className="custom_select">
                    <Select
                      showSearch
                      allowClear
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        option.label
                          .toLowerCase()
                          .indexOf(input.toLowerCase()) >= 0
                      }
                      placeholder="Select"
                      options={kpiOptions}
                      name="kpi_name"
                      value={kpiInputData?.kpi_name || null}
                      onChange={(e) => {
                        onChangeKpiInputData("kpi_name", e);
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row>
                <Col lg={4} md={6} sm={12}>
                  <Form.Item label="Start Date">
                    <DatePicker
                      status={dateErrors ? "error" : ""}
                      placeholder="Select Start Date"
                      format={{
                        format: "DD-MM-YYYY",
                        type: "mask",
                      }}
                      value={getDatePickerValue(kpiInputData?.start_date)}
                      onChange={(e) => {
                        onChangeKpiInputData("start_date", e);
                      }}
                    />
                  </Form.Item>
                </Col>

                <Col lg={4} md={6} sm={12}>
                  <Form.Item label="End Date">
                    <DatePicker
                      status={dateErrors ? "error" : ""}
                      placeholder="Select End Date"
                      format={{
                        format: "DD-MM-YYYY",
                        type: "mask",
                      }}
                      value={getDatePickerValue(kpiInputData?.end_date)}
                      onChange={(e) => {
                        onChangeKpiInputData("end_date", e);
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {!!dateErrors && (
              <h6 className="text-danger my-0 py-0">{dateErrors}</h6>
            )}

            <div className="cmn_table_wrap py-3">
              <Table
                columns={kpiDetails?.length ? columnsUpdated : columnsInitial}
                dataSource={kpiDetails?.length ? kpiDetails : []}
                pagination={false}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default memo(Kpi);
